import { useParams, Link } from 'react-router-dom';
import { Loader2, Eye, AlertCircle } from 'lucide-react';
import parse from 'html-react-parser';
import { useBlogPost } from '../hooks/useBlogPosts';
import { RelatedBlogPosts } from '../components/RelatedBlogPosts';
import { usePregnancyStore } from '@/store/pregnancyStore';

export function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { post, isLoading, error } = useBlogPost(slug);
  const { currentSSW } = usePregnancyStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <AlertCircle className="w-12 h-12 text-white/30 mb-4" />
        <p className="text-white/60 text-center">{error || 'Artikel nicht gefunden'}</p>
        <Link to="/blog" className="mt-4 text-pp-orange hover:underline">
          Alle Artikel anzeigen
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Title */}
      <h1 className="text-white text-2xl font-bold mb-6">
        {post.title}
      </h1>

      {/* Hero Image */}
      {post.image_url && (
        <div className="w-full aspect-video rounded-[15px] overflow-hidden mb-6">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Meta Tags */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {post.category && (
          <span className="px-3 py-1 bg-pp-teal border border-white/25 rounded-[5px] text-white text-sm font-medium">
            {post.category.name}
          </span>
        )}
        {post.ssw_min && post.ssw_max && (
          <span className="px-3 py-1 bg-pp-teal border border-white/25 rounded-[5px] text-white text-sm font-medium">
            SSW {post.ssw_min === post.ssw_max ? post.ssw_min : `${post.ssw_min}-${post.ssw_max}`}
          </span>
        )}
      </div>

      {/* Post meta */}
      {post.view_count > 0 && (
        <div className="flex items-center gap-4 text-sm text-white/60 mb-6 pb-6 border-b border-white/15">
          <span className="flex items-center gap-1.5">
            <Eye className="w-4 h-4" />
            {post.view_count} Aufrufe
          </span>
        </div>
      )}

      {/* Summary (if exists) */}
      {post.summary && (
        <div className="glass-effect border-l-4 border-pp-orange p-4 rounded-r-[15px] mb-6">
          <div className="text-white font-medium prose prose-sm prose-invert max-w-none">
            {parse(post.summary)}
          </div>
        </div>
      )}

      {/* Content */}
      <article className="prose prose-invert max-w-none prose-headings:text-white prose-p:text-white/80 prose-strong:text-white prose-a:text-pp-orange hover:prose-a:text-amber-400 prose-li:text-white/80 prose-blockquote:border-pp-orange prose-blockquote:text-white/90">
        {parse(post.content)}
      </article>

      {/* Related Posts */}
      {currentSSW && (
        <div className="mt-8 pt-6 border-t border-white/15">
          <h3 className="text-white text-lg font-semibold mb-4">Weitere Artikel</h3>
          <RelatedBlogPosts ssw={currentSSW} limit={3} showHeader={false} />
        </div>
      )}
    </>
  );
}
